import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { PollWithQuestionsResponse } from '../../../../../api/types/settings/settings-all';
import { CreatePollDialog } from '../../dialogs/create-poll-dialog';
import { DuplicatePollDialog } from '../../dialogs/duplicate-poll-dialog';
import { EditPollNameDialog } from '../../dialogs/edit-poll-name-dialog';
import { DeletePollConfirmDialog } from '../../dialogs/delete-poll-confirm-dialog';
import { PollDetailsDialog } from './dialogs/poll-details-dialog';

export const ProfessorPollTab = observer(() => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [duplicatePoll, setDuplicatePoll] =
    useState<PollWithQuestionsResponse | null>(null);
  const [editPoll, setEditPoll] = useState<PollWithQuestionsResponse | null>(
    null,
  );
  const [deletePoll, setDeletePoll] =
    useState<PollWithQuestionsResponse | null>(null);
  const [viewPoll, setViewPoll] = useState<PollWithQuestionsResponse | null>(
    null,
  );

  const polls = globalSettingsStores.abstractPolls;

  const questionsCountTemplate = (poll: PollWithQuestionsResponse) => {
    if (!poll) return <span>0</span>;
    return <span>{poll.questions?.length || 0}</span>;
  };

  const actionsTemplate = (poll: PollWithQuestionsResponse) => {
    if (!poll) return null;
    return (
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-info"
          tooltip="צפייה במשוב"
          tooltipOptions={{ position: 'top' }}
          onClick={(e) => {
            e.stopPropagation();
            setViewPoll(poll);
          }}
        />
        <Button
          icon="pi pi-copy"
          className="p-button-rounded p-button-text p-button-secondary"
          tooltip="העתקת משוב"
          tooltipOptions={{ position: 'top' }}
          onClick={(e) => {
            e.stopPropagation();
            setDuplicatePoll(poll);
          }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-warning"
          tooltip="עריכת שם"
          tooltipOptions={{ position: 'top' }}
          onClick={(e) => {
            e.stopPropagation();
            setEditPoll(poll);
          }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          tooltip="מחיקה"
          tooltipOptions={{ position: 'top' }}
          onClick={(e) => {
            e.stopPropagation();
            setDeletePoll(poll);
          }}
        />
      </div>
    );
  };

  return (
    <div style={{ marginTop: '16px' }}>
      <Dialog
        header="יצירת משוב חדש"
        visible={showCreateDialog}
        onHide={() => setShowCreateDialog(false)}
        style={{
          width: '500px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
      >
        <CreatePollDialog closeDialog={() => setShowCreateDialog(false)} />
      </Dialog>

      <Dialog
        header="העתקת משוב"
        visible={duplicatePoll !== null}
        onHide={() => setDuplicatePoll(null)}
        style={{
          width: '500px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
      >
        {duplicatePoll && (
          <DuplicatePollDialog
            originalPoll={duplicatePoll}
            closeDialog={() => setDuplicatePoll(null)}
          />
        )}
      </Dialog>

      <Dialog
        header="עריכת שם המשוב"
        visible={editPoll !== null}
        onHide={() => setEditPoll(null)}
        style={{
          width: '500px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
      >
        {editPoll && (
          <EditPollNameDialog
            poll={editPoll}
            closeDialog={() => setEditPoll(null)}
          />
        )}
      </Dialog>

      <Dialog
        header="מחיקת משוב"
        visible={deletePoll !== null}
        onHide={() => setDeletePoll(null)}
        style={{
          width: '450px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
      >
        {deletePoll && (
          <DeletePollConfirmDialog
            poll={deletePoll}
            closeDialog={() => setDeletePoll(null)}
          />
        )}
      </Dialog>

      <Dialog
        header="פרטי המשוב"
        visible={viewPoll !== null}
        onHide={() => setViewPoll(null)}
        style={{
          width: '700px',
          maxHeight: '90vh',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
        contentStyle={{ overflow: 'auto' }}
      >
        {viewPoll && (
          <PollDetailsDialog
            poll={viewPoll}
            closeDialog={() => setViewPoll(null)}
          />
        )}
      </Dialog>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ margin: 0 }}>רשימת משובים למרצים</h3>
        <Button
          label="הוספת משוב חדש"
          icon="pi pi-plus"
          onClick={() => setShowCreateDialog(true)}
          style={{ height: '36px' }}
        />
      </div>

      <DataTable
        value={polls}
        emptyMessage={
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <i
              className="pi pi-inbox"
              style={{ fontSize: '3rem', color: '#ccc', marginBottom: '16px' }}
            />
            <p style={{ color: '#888' }}>אין משובים במערכת</p>
          </div>
        }
        style={{ direction: 'rtl' }}
        sortField="id"
        sortOrder={-1}
        selectionMode="single"
        onRowClick={(e) => setViewPoll(e.data as PollWithQuestionsResponse)}
        rowClassName={() => 'cursor-pointer'}
      >
        <Column
          field="pollName"
          header="שם המשוב"
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          header="מספר שאלות"
          body={questionsCountTemplate}
          style={{ width: '120px', textAlign: 'right' }}
        />
        <Column
          header="פעולות"
          body={actionsTemplate}
          style={{ width: '200px', textAlign: 'center' }}
        />
      </DataTable>
    </div>
  );
});
